import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PasswordInput } from '@/components/auth/PasswordInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Loader2, Stethoscope, AlertCircle, ArrowLeft, Shield,
  CheckCircle, ChevronRight, ChevronLeft,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const STEPS = ['Account', 'Credentials', 'Practice', 'Consent'];

const SPECIALIZATIONS = [
  'General Medicine', 'Orthopedics', 'ENT', 'Dermatology', 'Cardiology',
  'Neurology', 'Pediatrics', 'Gynecology', 'Ophthalmology', 'Psychiatry',
  'Gastroenterology', 'Pulmonology', 'Urology', 'Nephrology', 'Oncology',
  'Endocrinology', 'Rheumatology', 'General Surgery', 'Plastic Surgery', 'Other',
];

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Delhi', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
  'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan',
  'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh',
  'Uttarakhand', 'West Bengal',
];

export default function DoctorSignup() {
  const [step, setStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Step 0: Account
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');

  // Step 1: Credentials
  const [licenseNumber, setLicenseNumber] = useState('');
  const [licenseState, setLicenseState] = useState('');
  const [specialization, setSpecialization] = useState('');
  const [yearsOfExperience, setYearsOfExperience] = useState('');
  const [degrees, setDegrees] = useState('');

  // Step 2: Practice
  const [clinicName, setClinicName] = useState('');
  const [practiceLocation, setPracticeLocation] = useState('');
  const [consultationLanguages, setConsultationLanguages] = useState('');
  const [feeMin, setFeeMin] = useState('');
  const [feeMax, setFeeMax] = useState('');
  const [bio, setBio] = useState('');

  // Step 3: Consent
  const [consentTelemedicine, setConsentTelemedicine] = useState(false);
  const [consentTerms, setConsentTerms] = useState(false);
  const [consentDataUsage, setConsentDataUsage] = useState(false);

  const { signUp } = useAuth();

  const canProceed = () => {
    switch (step) {
      case 0: return fullName.trim() && email.trim() && password.length >= 8;
      case 1: return licenseNumber.trim() && licenseState && specialization;
      case 2: return true; // Practice fields optional
      case 3: return consentTelemedicine && consentTerms && consentDataUsage;
      default: return false;
    }
  };

  const handleSubmit = async () => {
    setError(null);
    setIsLoading(true);

    try {
      // 1. Create auth account
      const { error: signUpError } = await signUp(email, password, fullName);
      if (signUpError) {
        setError(signUpError.message);
        setIsLoading(false);
        return;
      }

      // 2. Sign in immediately to get session
      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
      if (signInError) {
        setError('Account created. Please sign in at the doctor login page to complete setup.');
        setIsLoading(false);
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('Could not retrieve user session after signup.');
        setIsLoading(false);
        return;
      }

      // 3. Submit verification request
      const { error: vrError } = await supabase.from('verification_requests').insert({
        doctor_id: user.id,
        full_name: fullName,
        email,
        phone: phone || null,
        license_number: licenseNumber,
        license_state: licenseState,
        specializations: specialization ? [specialization] : [],
        years_of_experience: yearsOfExperience ? parseInt(yearsOfExperience, 10) : null,
        degrees: degrees || null,
        clinic_name: clinicName || null,
        practice_location: practiceLocation || null,
        consultation_languages: consultationLanguages || null,
        fee_min: feeMin ? parseInt(feeMin, 10) : null,
        fee_max: feeMax ? parseInt(feeMax, 10) : null,
        bio: bio || null,
        consent_telemedicine: consentTelemedicine,
        consent_terms: consentTerms,
        consent_data_usage: consentDataUsage,
      });

      if (vrError) {
        setError(`Verification request failed: ${vrError.message}`);
        setIsLoading(false);
        return;
      }

      // 4. Sign out — doctor must wait for approval before accessing console
      await supabase.auth.signOut();
      setSuccess(true);
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div
          className="w-full max-w-md text-center"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="flex justify-center mb-6">
            <div className="p-5 rounded-full bg-green-500/10 border border-green-500/30">
              <CheckCircle className="h-12 w-12 text-green-500" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-3">Application Submitted</h1>
          <p className="text-muted-foreground mb-2">
            Your doctor registration has been received. Our team will verify your credentials and
            notify you via email within <strong>2–3 business days</strong>.
          </p>
          <p className="text-sm text-muted-foreground mb-8">
            You will not be able to access the doctor console until your account is approved.
          </p>
          <Button variant="outline" asChild className="w-full">
            <Link to="/doctor/login">Back to Doctor Login</Link>
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 grid-pattern opacity-30" />
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl animate-float-slow" />
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-float-delayed" />

      <motion.div
        className="w-full max-w-lg relative z-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Link
          to="/doctor/login"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6 group"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
          Back to doctor login
        </Link>

        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <Stethoscope className="h-8 w-8 text-secondary" />
          <span className="text-xl font-bold text-foreground">
            Apne<span className="text-primary">Doctors</span>{' '}
            <span className="text-secondary text-sm font-normal">· Doctor Application</span>
          </span>
        </div>

        {/* Step indicators */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div
                className={cn(
                  'flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold transition-colors',
                  i < step
                    ? 'bg-green-500 text-white'
                    : i === step
                    ? 'bg-secondary text-white'
                    : 'bg-muted text-muted-foreground',
                )}
              >
                {i < step ? <CheckCircle className="h-4 w-4" /> : i + 1}
              </div>
              <span
                className={cn(
                  'text-xs hidden sm:block',
                  i === step ? 'text-foreground font-medium' : 'text-muted-foreground',
                )}
              >
                {s}
              </span>
              {i < STEPS.length - 1 && (
                <div className={cn('h-px w-6 transition-colors', i < step ? 'bg-green-500' : 'bg-border')} />
              )}
            </div>
          ))}
        </div>

        <Card className="border-border/30 bg-card/80 backdrop-blur-xl shadow-2xl overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-secondary via-primary to-secondary" />

          <CardHeader className="pb-2 pt-6 px-6">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-secondary" />
              <h2 className="font-semibold text-foreground">
                Step {step + 1}: {STEPS[step]}
              </h2>
            </div>
          </CardHeader>

          <CardContent className="px-6 space-y-4">
            {error && (
              <Alert variant="destructive" className="border-destructive/30 bg-destructive/10">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* STEP 0: Account */}
            {step === 0 && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="fullName">Full Legal Name *</Label>
                  <Input
                    id="fullName"
                    placeholder="Dr. Firstname Lastname"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Professional Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="doctor@hospital.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+91 9876543210"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <PasswordInput
                    id="password"
                    placeholder="Minimum 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                  <p className="text-xs text-muted-foreground">Minimum 8 characters.</p>
                </div>
              </>
            )}

            {/* STEP 1: Credentials */}
            {step === 1 && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="license">Medical License Number *</Label>
                  <Input
                    id="license"
                    placeholder="MH-12345 / IMC-67890"
                    value={licenseNumber}
                    onChange={(e) => setLicenseNumber(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="licenseState">Issuing State Council *</Label>
                  <Select value={licenseState} onValueChange={setLicenseState}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select state..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="IMC">Indian Medical Council (Central)</SelectItem>
                      {INDIAN_STATES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="spec">Primary Specialization *</Label>
                  <Select value={specialization} onValueChange={setSpecialization}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select specialization..." />
                    </SelectTrigger>
                    <SelectContent>
                      {SPECIALIZATIONS.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="exp">Years of Experience</Label>
                    <Input
                      id="exp"
                      type="number"
                      min={0}
                      max={60}
                      placeholder="e.g. 8"
                      value={yearsOfExperience}
                      onChange={(e) => setYearsOfExperience(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="degrees">Degrees</Label>
                    <Input
                      id="degrees"
                      placeholder="MBBS, MD, MS..."
                      value={degrees}
                      onChange={(e) => setDegrees(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </>
            )}

            {/* STEP 2: Practice */}
            {step === 2 && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="clinic">Clinic / Hospital Name</Label>
                  <Input
                    id="clinic"
                    placeholder="Apollo Hospital, Mumbai"
                    value={clinicName}
                    onChange={(e) => setClinicName(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location">Practice Location</Label>
                  <Input
                    id="location"
                    placeholder="City, State"
                    value={practiceLocation}
                    onChange={(e) => setPracticeLocation(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="langs">Consultation Languages</Label>
                  <Input
                    id="langs"
                    placeholder="English, Hindi, Marathi..."
                    value={consultationLanguages}
                    onChange={(e) => setConsultationLanguages(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="feeMin">Min Fee (₹)</Label>
                    <Input
                      id="feeMin"
                      type="number"
                      min={0}
                      placeholder="300"
                      value={feeMin}
                      onChange={(e) => setFeeMin(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="feeMax">Max Fee (₹)</Label>
                    <Input
                      id="feeMax"
                      type="number"
                      min={0}
                      placeholder="1500"
                      value={feeMax}
                      onChange={(e) => setFeeMax(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bio">Professional Bio</Label>
                  <Textarea
                    id="bio"
                    placeholder="Brief description of your practice and expertise..."
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    disabled={isLoading}
                    rows={3}
                  />
                </div>
              </>
            )}

            {/* STEP 3: Consent */}
            {step === 3 && (
              <>
                <p className="text-sm text-muted-foreground">
                  Please read and accept all three statements to complete your application.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-3 rounded-lg border border-border/50 bg-muted/20">
                    <Checkbox
                      id="consentTelemedicine"
                      checked={consentTelemedicine}
                      onCheckedChange={(v) => setConsentTelemedicine(!!v)}
                    />
                    <Label htmlFor="consentTelemedicine" className="text-sm leading-relaxed cursor-pointer">
                      I consent to providing medical consultations via telemedicine on the ApneDoctors
                      platform and understand the associated responsibilities and limitations.
                    </Label>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg border border-border/50 bg-muted/20">
                    <Checkbox
                      id="consentTerms"
                      checked={consentTerms}
                      onCheckedChange={(v) => setConsentTerms(!!v)}
                    />
                    <Label htmlFor="consentTerms" className="text-sm leading-relaxed cursor-pointer">
                      I have read and agree to the ApneDoctors{' '}
                      <Link to="/terms" className="text-primary underline" target="_blank">
                        Terms of Service
                      </Link>{' '}
                      and{' '}
                      <Link to="/privacy" className="text-primary underline" target="_blank">
                        Privacy Policy
                      </Link>
                      .
                    </Label>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg border border-border/50 bg-muted/20">
                    <Checkbox
                      id="consentData"
                      checked={consentDataUsage}
                      onCheckedChange={(v) => setConsentDataUsage(!!v)}
                    />
                    <Label htmlFor="consentData" className="text-sm leading-relaxed cursor-pointer">
                      I consent to the collection and processing of my professional data for identity
                      verification and platform operation purposes.
                    </Label>
                  </div>
                </div>
                <Alert className="border-secondary/30 bg-secondary/5 mt-2">
                  <Shield className="h-4 w-4 text-secondary" />
                  <AlertDescription className="text-xs text-muted-foreground">
                    Your credentials will be manually reviewed before your account is activated.
                    This typically takes 2–3 business days.
                  </AlertDescription>
                </Alert>
              </>
            )}
          </CardContent>

          <CardFooter className="flex gap-3 px-6 pb-6">
            {step > 0 && (
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => { setError(null); setStep(step - 1); }}
                disabled={isLoading}
              >
                <ChevronLeft className="h-4 w-4 mr-1" /> Back
              </Button>
            )}

            {step < STEPS.length - 1 ? (
              <Button
                type="button"
                className="flex-1 bg-secondary hover:bg-secondary/90"
                onClick={() => { setError(null); setStep(step + 1); }}
                disabled={!canProceed() || isLoading}
              >
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button
                type="button"
                className="flex-1 bg-secondary hover:bg-secondary/90"
                onClick={handleSubmit}
                disabled={!canProceed() || isLoading}
              >
                {isLoading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</>
                ) : (
                  'Submit Application'
                )}
              </Button>
            )}
          </CardFooter>
        </Card>

        <p className="text-xs text-muted-foreground text-center mt-4">
          Already applied?{' '}
          <Link to="/doctor/login" className="text-secondary hover:underline">
            Sign in here
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
